"use client";

import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarIcon } from "lucide-react";
import { isEqual } from "date-fns";

import { Calendar } from "@/ui/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover";
import { Button } from "@/ui/components/ui/button";
import {
  instantFromZonedWallClock,
  zonedDayString,
  zonedTimeString,
} from "@/shared/utils/date/zoned-wall-clock.ts";

export interface DateTimeRangeValue {
  fromDate: Date;
  toDate: Date;
}

interface DateTimeCustomPickerProps {
  value: DateTimeRangeValue;
  onChange: (val: DateTimeRangeValue) => void;
  /** The location's IANA zone — every field reads AND writes wall clock in it. */
  timeZone: string;
}

/**
 * Format a Date as a `YYYY-MM-DDTHH:mm` string for <input type="datetime-local">,
 * reading the wall clock in `timeZone`. Every handler below parses back through
 * `instantFromZonedWallClock` with the same zone, so what the field shows is
 * always exactly what gets constructed.
 */
const toDatetimeLocal = (date: Date, timeZone: string): string =>
  `${zonedDayString(date, timeZone)}T${zonedTimeString(date, timeZone)}`;

/**
 * A browser-local stand-in Date carrying the zone's calendar day, for the
 * `Calendar` component — react-day-picker reads local components, so it must be
 * handed the day the user sees, not the day the instant falls on locally.
 */
const toCalendarDay = (date: Date, timeZone: string): Date => {
  const [year, month, day] = zonedDayString(date, timeZone).split("-").map(Number);
  return new Date(year, month - 1, day);
};

export default function DateTimeCustomPicker({
  value,
  onChange,
  timeZone,
}: DateTimeCustomPickerProps) {
  const { t } = useTranslation("common");
  const { fromDate, toDate } = value;
  const lastEmitted = useRef<DateTimeRangeValue | null>(null);

  // Sync lastEmitted when value changes from parent (e.g., preset selection)
  useEffect(() => {
    if (!lastEmitted.current ||
        !isEqual(lastEmitted.current.fromDate, fromDate) ||
        !isEqual(lastEmitted.current.toDate, toDate)) {
      lastEmitted.current = { fromDate, toDate };
    }
  }, [fromDate, toDate]);

  const emitChange = (newFrom: Date, newTo: Date) => {
    const hasChanged =
      !lastEmitted.current ||
      !isEqual(lastEmitted.current.fromDate, newFrom) ||
      !isEqual(lastEmitted.current.toDate, newTo);

    if (hasChanged) {
      lastEmitted.current = { fromDate: newFrom, toDate: newTo };
      onChange({ fromDate: newFrom, toDate: newTo });
    }
  };

  /** Keep the instant's day in `timeZone`, replace its wall-clock time there. */
  const updateTime = (date: Date, timeString: string): Date => {
    const [hours, minutes] = timeString.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return date;

    return instantFromZonedWallClock(
      `${zonedDayString(date, timeZone)} ${timeString}:00`,
      timeZone,
    );
  };

  /** Keep the instant's wall-clock time in `timeZone`, replace its day there. */
  const handleDateSelect = (date: Date | undefined, isFromDate: boolean) => {
    if (!date) return;

    const currentDate = isFromDate ? fromDate : toDate;
    // `date` is the calendar's browser-local stand-in — read back the same
    // components `toCalendarDay` wrote into it.
    const pad = (n: number) => String(n).padStart(2, "0");
    const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const newDate = instantFromZonedWallClock(
      `${day} ${zonedTimeString(currentDate, timeZone)}:00`,
      timeZone,
    );

    if (isFromDate) {
      emitChange(newDate, toDate);
    } else {
      emitChange(fromDate, newDate);
    }
  };

  const handleTimeChange = (timeString: string, isFromDate: boolean) => {
    const currentDate = isFromDate ? fromDate : toDate;
    const newDate = updateTime(currentDate, timeString);

    if (isFromDate) {
      emitChange(newDate, toDate);
    } else {
      emitChange(fromDate, newDate);
    }
  };

  // Typed entry via the datetime-local input (faster than the calendar). The
  // value is a zone-less `YYYY-MM-DDTHH:mm` string, read AS the location's wall
  // clock (`new Date(val)` would have read it as the browser's); ignore
  // incomplete/invalid input.
  const handleDateTimeInput = (val: string, isFromDate: boolean) => {
    if (!val) return;
    const parsed = instantFromZonedWallClock(val, timeZone);
    if (isNaN(parsed.getTime())) return;

    if (isFromDate) {
      emitChange(parsed, toDate);
    } else {
      emitChange(fromDate, parsed);
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      {([true, false] as const).map((isFromDate) => {
        const date = isFromDate ? fromDate : toDate;
        return (
          <div key={isFromDate ? "from" : "to"} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">
              {isFromDate ? t("dateRange.custom.from") : t("dateRange.custom.to")}
            </label>
            <div className="flex gap-1.5">
              {/* Typeable input — fast keyboard entry of the full date + time. */}
              <input
                type="datetime-local"
                value={toDatetimeLocal(date, timeZone)}
                onChange={(e) => handleDateTimeInput(e.target.value, isFromDate)}
                className="flex-1 rounded-md border px-2 py-1.5 text-sm bg-background"
              />
              {/* Calendar popover — visual pick, still available. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={
                      isFromDate
                        ? t("dateRange.custom.openFromCalendar")
                        : t("dateRange.custom.openToCalendar")
                    }
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={toCalendarDay(date, timeZone)}
                    onSelect={(d) => handleDateSelect(d, isFromDate)}
                    initialFocus
                  />
                  <div className="p-2 border-t flex justify-center">
                    <input
                      type="time"
                      value={zonedTimeString(date, timeZone)}
                      className="w-[120px] rounded-md border px-2 py-1 text-sm bg-background"
                      onChange={(e) => handleTimeChange(e.target.value, isFromDate)}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        );
      })}
    </div>
  );
}
